/**
 * O catálogo de desenhos internos — o SVG do miolo de um componente no diagrama.
 *
 * **A lista não traz o desenho, e é de propósito.** São 2.745 desenhos e 4 MB de
 * SVG somados; o traço vem só no `obter` de um. O que a lista dá no lugar é o
 * `tamanhoDoDesenho`, que já diz se há traço ali — 31 estão vazios.
 *
 * **As três contagens de uso não são a mesma coisa**, e a tela precisa mostrar
 * as três:
 *
 *  - `emUso` — componentes de diagrama que apontam para o desenho, contados
 *    apenas quando o componente ainda existe;
 *  - `diagramas` — em quantos diagramas distintos o traço aparece, que é o
 *    tamanho real do estrago de uma exclusão;
 *  - `orfaos` — ligações cujo componente sumiu. São 25.309 das 109.200 em
 *    produção (23%), e é por isso que a contagem crua mente.
 */

import { api, foraDoProjeto, obter } from "./cliente"
import type { PaginaResponse } from "./tipos"

export type DesenhoInternoResumo = {
  id: number
  titulo: string
  qtdPinos: number
  qtdPinosOposto: number
  posicao: string
  alturaMinima: number
  referencia: number
  referenciaTexto: string
  componentePrincipal: boolean
  deslocarPino: boolean
  tamanhoDoDesenho: number
  emUso: number
  diagramas: number
  orfaos: number
  faqs: number
}

export type FaqDoDesenho = { faqId: number; pergunta: string }

export type DesenhoInterno = Omit<DesenhoInternoResumo, "tamanhoDoDesenho" | "faqs"> & {
  desenho: string
  faqs: FaqDoDesenho[]
}

export type DiagramaDoDesenho = {
  diagramaId: number
  projetoId: number
  projeto: string
  componenteId: number
  componenteCodigo: string
  componenteDescricao: string
  quantos: number
}

export type SalvarDesenhoInterno = {
  titulo: string
  desenho?: string | null
  qtdPinos: number
  qtdPinosOposto: number
  posicao: string
  alturaMinima: number
  referencia: number
  componentePrincipal: boolean
  deslocarPino: boolean
}

export function listarDesenhosInternos(p: {
  busca?: string
  posicao?: string
  semUso?: boolean
  pagina?: number
  porPagina?: number
  /** Sem ordem, a API ordena pelo título — é o que o seletor do diagrama quer. */
  ordem?: "inclusao" | "alfabetica"
}) {
  return obter<PaginaResponse<DesenhoInternoResumo>>("api/desenhos-internos", {
    busca: p.busca || undefined,
    posicao: p.posicao || undefined,
    semUso: p.semUso,
    pagina: p.pagina ?? 1,
    porPagina: p.porPagina ?? 100,
    ordem: p.ordem,
  })
}

export function obterDesenhoInterno(id: number) {
  return obter<DesenhoInterno>(`api/desenhos-internos/${id}`, {})
}

export function diagramasDoDesenho(id: number, pagina = 1) {
  return obter<PaginaResponse<DiagramaDoDesenho>>(`api/desenhos-internos/${id}/diagramas`, {
    pagina,
    porPagina: 50,
  })
}

export async function criarDesenhoInterno(dados: SalvarDesenhoInterno) {
  const r = await api.post<DesenhoInterno>("api/desenhos-internos", dados, foraDoProjeto())
  return r.data
}

export async function alterarDesenhoInterno(id: number, dados: SalvarDesenhoInterno) {
  const r = await api.put<DesenhoInterno>(`api/desenhos-internos/${id}`, dados, foraDoProjeto())
  return r.data
}

/**
 * Exclui. **Com `desprender`, tira o traço de todos os diagramas que o usam** —
 * é o que o sistema antigo faz sempre e sem avisar. Sem ele, a API recusa
 * enquanto houver uso, dizendo quantos componentes e quantos diagramas.
 */
export async function excluirDesenhoInterno(id: number, desprender = false) {
  const r = await api.delete(
    `api/desenhos-internos/${id}${desprender ? "?desprender=true" : ""}`,
    foraDoProjeto(),
  )
  return Number(r.headers["x-componentes-desprendidos"] ?? 0)
}

/** O conjunto inteiro: o que vier passa a ser o que o desenho tem. */
export function salvarFaqsDoDesenho(id: number, faqIds: number[]) {
  return api.put(`api/desenhos-internos/${id}/faqs`, { faqIds }, foraDoProjeto())
}
